// src/pages/QuizPage/index.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLang } from "../../context/LangContext";

// Utils
import { shuffleArray, getShuffleSettings } from "./utils/shuffleUtils";

// Hooks
import { useMarafonQuiz } from "./hooks/useMarafonQuiz";
import { useSessionQuiz } from "./hooks/useSessionQuiz";
import { useMistakesQuiz } from "./hooks/useMistakesQuiz";
import { useFixedQuestionsQuiz } from "./hooks/useFixedQuestionsQuiz";
import { useQuizKeyboard } from "./hooks/useQuizKeyboard";
import { murakkabAPI, sonliAPI } from "../../api";

// Components
import QuizTopbar from "./components/QuizTopbar";
import QuestionBody from "./components/QuestionBody";
import QuestionDots from "./components/QuestionDots";
import QuizResult from "./components/QuizResult";
import SignsView from "./components/SignsView";
import { QuizLoading, QuizError } from "./components/QuizLoadingError";
import toast from "react-hot-toast";

const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

const QuizPage: React.FC = () => {
  const { mode, id, subId } = useParams();
  const navigate = useNavigate();
  const { lang, setLang, t } = useLang();

  // ─── Mode flags ─────────────────────────────────────────────────────
  const isMarafonMode = mode === "marafon";
  const isMistakeMode = mode === "mistakes";
  const isMurakkabMode = mode === "murakkab";
  const isSonliMode = mode === "sonli";
  const isSignsMode = mode === "signs";
  const isTopicMode = mode === "topic";
  const isTicketMode = mode === "ticket" || mode === "tickets";
  const isNazoratMode = mode === "nazorat" || mode === "exam";

  // ─── Fullscreen ─────────────────────────────────────────────────────
  const [isFullscreen, setIsFullscreen] = useState(
    isIOS
      ? (window.navigator as any).standalone === true
      : !!document.fullscreenElement,
  );

  useEffect(() => {
    if (isIOS) return;
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  const toggleFullscreen = () => {
    if (isIOS) {
      if (!(window.navigator as any).standalone) {
        toast("📱 Safari → Ulashish (☐↑) → Bosh ekranga qo'shish", { duration: 4000 });
      }
      return;
    }
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // ─── Signs mode — alohida render, hook kerakmas ────────────────────
  if (isSignsMode) {
    return <SignsView categoryId={subId} lang={lang} setLang={setLang} />;
  }

  // ─── Mode'ga qarab tegishli hook'ni ishlatamiz ─────────────────────
  // Eslatma: Conditional hook chaqirish — React rules buziladi.
  // Shuning uchun har bir mode uchun alohida sub-component.
  if (isMarafonMode) {
    return (
      <MarafonQuizMode
        lang={lang}
        setLang={setLang}
        t={t}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
      />
    );
  }

  if (isMistakeMode) {
    return (
      <MistakesQuizMode
        lang={lang}
        setLang={setLang}
        t={t}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
      />
    );
  }

  if (isMurakkabMode || isSonliMode) {
    return (
      <FixedQuizMode
        mode={isMurakkabMode ? "murakkab" : "sonli"}
        fetchQuestions={
          isMurakkabMode ? murakkabAPI.getQuestions : sonliAPI.getQuestions
        }
        lang={lang}
        setLang={setLang}
        t={t}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
      />
    );
  }

  // Default: session-based modes (topic, ticket, nazorat, exam, random)
  return (
    <SessionQuizMode
      mode={mode as any}
      id={id}
      lang={lang}
      setLang={setLang}
      t={t}
      isFullscreen={isFullscreen}
      toggleFullscreen={toggleFullscreen}
      isTopicMode={isTopicMode}
      isTicketMode={isTicketMode}
      isNazoratMode={isNazoratMode}
    />
  );
};

export default QuizPage;

// ═══════════════════════════════════════════════════════════════════
// SUB-COMPONENTS — har biri o'z hook'ini ishlatadi
// ═══════════════════════════════════════════════════════════════════

interface ModeProps {
  lang: "oz" | "uz" | "ru";
  setLang: (lang: "oz" | "uz" | "ru") => void;
  t: (key: string) => string;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
}

// ─── MARAFON MODE ──────────────────────────────────────────────────
const MarafonQuizMode: React.FC<ModeProps> = ({
  lang,
  setLang,
  t,
  isFullscreen,
  toggleFullscreen,
}) => {
  const {
    questions,
    current,
    answered,
    lives,
    loading,
    error,
    finished,
    correctCount,
    wrongCount,
    handleAnswer,
    setCurrent,
    finish,
    reset,
  } = useMarafonQuiz({ lang });

  const currentQ = questions[current];
  const displayChoices = useMemo(() => {
    if (!currentQ) return [];
    const { shuffleAnswers } = getShuffleSettings();
    return shuffleAnswers ? shuffleArray(currentQ.choices) : currentQ.choices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ?.id]);

  useQuizKeyboard({
    finished,
    isMarafonMode: true,
    current,
    answered,
    questionsLength: questions.length,
    handleAnswer,
    setCurrent,
    displayChoices,
  });

  if (loading) return <QuizLoading />;
  if (error) return <QuizError message={error} onRetry={reset} />;

  if (finished) {
    return (
      <QuizResult
        mode="marafon"
        questionsLength={questions.length}
        correctCount={correctCount}
        wrongCount={wrongCount}
        isMarafonMode
        isMistakeMode={false}
        isMistakeEmpty={false}
        isTopicMode={false}
        isTicketMode={false}
        isNazoratMode={false}
        onRetry={reset}
        t={t}
      />
    );
  }

  const q = questions[current];
  if (!q) return <QuizLoading />;

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white flex flex-col">
      <QuizTopbar
        lang={lang}
        setLang={setLang}
        isMarafonMode
        lives={lives}
        hasTimer={false}
        timeLeft={0}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        onFinish={finish}
      />
      {/* Mobile: raqamlar topbar ostida */}
      <div className="lg:hidden">
        <QuestionDots
          questions={questions}
          current={current}
          answered={answered}
          onSelect={setCurrent}
        />
      </div>
      <QuestionBody
        question={q}
        current={current}
        answered={answered}
        onAnswer={handleAnswer}
        displayChoices={displayChoices}
        lang={lang}
      />
      {/* Desktop: raqamlar pastda */}
      <div className="hidden lg:block">
        <QuestionDots
          questions={questions}
          current={current}
          answered={answered}
          onSelect={setCurrent}
        />
      </div>
    </div>
  );
};

// ─── MISTAKES MODE ─────────────────────────────────────────────────
const MistakesQuizMode: React.FC<ModeProps> = ({
  lang,
  setLang,
  t,
  isFullscreen,
  toggleFullscreen,
}) => {
  const {
    questions,
    current,
    answered,
    loading,
    error,
    finished,
    isEmpty,
    correctCount,
    wrongCount,
    handleAnswer,
    setCurrent,
    finish,
    reset,
  } = useMistakesQuiz({ lang });

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    const dy = Math.abs(touchStartY.current - e.changedTouches[0].clientY);
    if (dy > Math.abs(dx) || Math.abs(dx) < 50) return;
    if (dx > 0 && current < questions.length - 1) setCurrent(current + 1);
    else if (dx < 0 && current > 0) setCurrent(current - 1);
  };

  const mistakesCurrentQ = questions[current];
  const mistakesDisplayChoices = useMemo(() => {
    if (!mistakesCurrentQ) return [];
    const { shuffleAnswers } = getShuffleSettings();
    return shuffleAnswers
      ? shuffleArray(mistakesCurrentQ.choices)
      : mistakesCurrentQ.choices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mistakesCurrentQ?.id]);

  useQuizKeyboard({
    finished,
    isMarafonMode: false,
    current,
    answered,
    questionsLength: questions.length,
    handleAnswer,
    setCurrent,
    displayChoices: mistakesDisplayChoices,
  });

  if (loading) return <QuizLoading />;
  if (error) return <QuizError message={error} onRetry={reset} />;

  if (finished) {
    return (
      <QuizResult
        mode="mistakes"
        questionsLength={questions.length}
        correctCount={correctCount}
        wrongCount={wrongCount}
        isMarafonMode={false}
        isMistakeMode
        isMistakeEmpty={isEmpty}
        isTopicMode={false}
        isTicketMode={false}
        isNazoratMode={false}
        onRetry={reset}
        t={t}
      />
    );
  }

  const q = questions[current];
  if (!q) return <QuizLoading />;

  return (
    <div
      className="min-h-screen bg-[#0A0E1A] text-white flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}>
      <QuizTopbar
        lang={lang}
        setLang={setLang}
        isMarafonMode={false}
        lives={0}
        hasTimer={false}
        timeLeft={0}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        onFinish={finish}
      />
      {/* Mobile: raqamlar topbar ostida */}
      <div className="lg:hidden">
        <QuestionDots
          questions={questions}
          current={current}
          answered={answered}
          onSelect={setCurrent}
        />
      </div>
      <QuestionBody
        question={q}
        current={current}
        answered={answered}
        onAnswer={handleAnswer}
        displayChoices={mistakesDisplayChoices}
        lang={lang}
      />
      {/* Desktop: raqamlar pastda (eski holat) */}
      <div className="hidden lg:block">
        <QuestionDots
          questions={questions}
          current={current}
          answered={answered}
          onSelect={setCurrent}
        />
      </div>
    </div>
  );
};

// ─── FIXED QUIZ MODE (murakkab, sonli) ─────────────────────────────
interface FixedQuizModeProps extends ModeProps {
  mode: "murakkab" | "sonli";
  fetchQuestions: (
    lang: string,
    limit: number,
    exclude: number[],
  ) => Promise<{ data: any }>;
}

const FixedQuizMode: React.FC<FixedQuizModeProps> = ({
  mode,
  fetchQuestions,
  lang,
  setLang,
  t,
  isFullscreen,
  toggleFullscreen,
}) => {
  const {
    questions,
    current,
    answered,
    loading,
    error,
    finished,
    isEmpty,
    correctCount,
    wrongCount,
    handleAnswer,
    setCurrent,
    finish,
    reset,
  } = useFixedQuestionsQuiz({ lang, fetchQuestions });

  const navigate = useNavigate();

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    const dy = Math.abs(touchStartY.current - e.changedTouches[0].clientY);
    if (dy > Math.abs(dx) || Math.abs(dx) < 50) return;
    if (dx > 0 && current < questions.length - 1) setCurrent(current + 1);
    else if (dx < 0 && current > 0) setCurrent(current - 1);
  };

  const currentQ = questions[current];
  const displayChoices = useMemo(() => {
    if (!currentQ) return [];
    const { shuffleAnswers } = getShuffleSettings();
    return shuffleAnswers ? shuffleArray(currentQ.choices) : currentQ.choices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ?.id]);

  useQuizKeyboard({
    finished,
    isMarafonMode: false,
    current,
    answered,
    questionsLength: questions.length,
    handleAnswer,
    setCurrent,
    displayChoices,
  });

  if (loading) return <QuizLoading />;
  if (error) return <QuizError message={error} onRetry={reset} />;

  if (isEmpty) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0E1A] px-4">
        <div className="bg-[#0D1526] border border-white/10 p-8 rounded-[32px] max-w-md w-full text-center shadow-2xl">
          <h2 className="text-2xl font-black text-white mb-3">
            {t("noTypedQuestions")}
          </h2>
          <p className="text-gray-400 text-sm mb-8">
            {t("noTypedQuestionsDesc")}
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-500 transition">
            Bosh sahifaga qaytish
          </button>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <QuizResult
        mode={mode}
        questionsLength={questions.length}
        correctCount={correctCount}
        wrongCount={wrongCount}
        isMarafonMode={false}
        isMistakeMode={false}
        isMistakeEmpty={false}
        isTopicMode={false}
        isTicketMode={false}
        isNazoratMode={false}
        onRetry={reset}
        t={t}
      />
    );
  }

  const q = questions[current];
  if (!q) return <QuizLoading />;

  return (
    <div
      className="min-h-screen bg-[#0A0E1A] text-white flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}>
      <QuizTopbar
        lang={lang}
        setLang={setLang}
        isMarafonMode={false}
        lives={0}
        hasTimer={false}
        timeLeft={0}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        onFinish={finish}
      />
      <div className="lg:hidden">
        <QuestionDots
          questions={questions}
          current={current}
          answered={answered}
          onSelect={setCurrent}
        />
      </div>
      <QuestionBody
        question={q}
        current={current}
        answered={answered}
        onAnswer={handleAnswer}
        displayChoices={displayChoices}
        lang={lang}
      />
      <div className="hidden lg:block">
        <QuestionDots
          questions={questions}
          current={current}
          answered={answered}
          onSelect={setCurrent}
        />
      </div>
    </div>
  );
};

// ─── SESSION MODE (topic, ticket, nazorat, exam, random) ───────────
interface SessionModeProps extends ModeProps {
  mode: "topic" | "ticket" | "tickets" | "nazorat" | "exam" | "random";
  id?: string;
  isTopicMode: boolean;
  isTicketMode: boolean;
  isNazoratMode: boolean;
}

const SessionQuizMode: React.FC<SessionModeProps> = ({
  mode,
  id,
  lang,
  setLang,
  t,
  isFullscreen,
  toggleFullscreen,
  isTopicMode,
  isTicketMode,
  isNazoratMode,
}) => {
  const {
    questions,
    current,
    answered,
    loading,
    error,
    finished,
    timeLeft,
    correctCount,
    wrongCount,
    hasTimer,
    handleAnswer,
    setCurrent,
    finish,
    reset,
  } = useSessionQuiz({ mode, id, lang });

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    const dy = Math.abs(touchStartY.current - e.changedTouches[0].clientY);
    if (dy > Math.abs(dx) || Math.abs(dx) < 50) return;
    if (dx > 0 && current < questions.length - 1) setCurrent(current + 1);
    else if (dx < 0 && current > 0) setCurrent(current - 1);
  };

  const sessionCurrentQ = questions[current];
  const sessionDisplayChoices = useMemo(() => {
    if (!sessionCurrentQ) return [];
    const { shuffleAnswers } = getShuffleSettings();
    return shuffleAnswers
      ? shuffleArray(sessionCurrentQ.choices)
      : sessionCurrentQ.choices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionCurrentQ?.id]);

  useQuizKeyboard({
    finished,
    isMarafonMode: false,
    current,
    answered,
    questionsLength: questions.length,
    handleAnswer,
    setCurrent,
    displayChoices: sessionDisplayChoices,
  });

  if (loading) return <QuizLoading />;
  if (error) return <QuizError message={error} onRetry={reset} />;

  if (finished) {
    return (
      <QuizResult
        mode={mode}
        questionsLength={questions.length}
        correctCount={correctCount}
        wrongCount={wrongCount}
        isMarafonMode={false}
        isMistakeMode={false}
        isMistakeEmpty={false}
        isTopicMode={isTopicMode}
        isTicketMode={isTicketMode}
        isNazoratMode={isNazoratMode}
        onRetry={reset}
        t={t}
      />
    );
  }

  const q = questions[current];
  if (!q) return <QuizLoading />;

  return (
    <div
      className="min-h-screen bg-[#0A0E1A] text-white flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}>
      <QuizTopbar
        lang={lang}
        setLang={setLang}
        isMarafonMode={false}
        lives={0}
        hasTimer={hasTimer}
        timeLeft={timeLeft}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        onFinish={finish}
      />
      {/* Mobile: raqamlar topbar ostida */}
      <div className="lg:hidden">
        <QuestionDots
          questions={questions}
          current={current}
          answered={answered}
          onSelect={setCurrent}
        />
      </div>
      <QuestionBody
        question={q}
        current={current}
        answered={answered}
        onAnswer={handleAnswer}
        displayChoices={sessionDisplayChoices}
        lang={lang}
      />
      {/* Desktop: raqamlar pastda (eski holat) */}
      <div className="hidden lg:block">
        <QuestionDots
          questions={questions}
          current={current}
          answered={answered}
          onSelect={setCurrent}
        />
      </div>
    </div>
  );
};
